import { z } from "zod";
import { permissionSchema } from "../auth/permissions.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
/**
 * Genel onay akışı motoru (Prompt 11). Motor GENELDİR: her modül kendi belge türüyle talep açar, kararı AYNI
 * transaction'da kayıtlı işleyicisiyle geri alır. Motorun özellik bayrağı YOKTUR; her kiracıda açıktır.
 */
// ── Belge türleri ────────────────────────────────────────────────────────────
/**
 * Bilinen belge türleri. Veritabanı türü serbest bir koddur (desen denetimi): yeni modül migration yazmadan
 * yeni tür ekleyebilir; sözleşme tarafında tür buraya eklenince politika ekranlarında da görünür.
 */
export const APPROVAL_DOC_TYPES = [
    "EXPENSE",
    "PRICE_CHANGE",
    "CREDIT_LIMIT_OVERRIDE",
    "DISCOUNT",
    "FUEL_ORDER",
    "CASH_COUNT_DIFFERENCE",
    "SHIFT_DIFFERENCE",
    "PAYMENT_BATCH",
    "TOTALIZER_ADJUSTMENT",
    "MARKET_ORDER",
    "STOCK_WRITE_OFF",
    "STOCK_COUNT",
    "INVOICE_MATCH_DIFFERENCE",
];
export const approvalDocTypeSchema = z.enum(APPROVAL_DOC_TYPES);
export const APPROVAL_DOC_TYPE_LABELS = {
    EXPENSE: "Gider belgesi",
    PRICE_CHANGE: "Fiyat değişikliği",
    CREDIT_LIMIT_OVERRIDE: "Kredi limiti aşımı",
    DISCOUNT: "İskonto",
    FUEL_ORDER: "Akaryakıt siparişi",
    CASH_COUNT_DIFFERENCE: "Kasa sayım farkı",
    SHIFT_DIFFERENCE: "Vardiya farkı",
    PAYMENT_BATCH: "Ödeme partisi",
    TOTALIZER_ADJUSTMENT: "Endeks düzeltmesi",
    MARKET_ORDER: "Market siparişi",
    STOCK_WRITE_OFF: "Stok fire / imha",
    STOCK_COUNT: "Stok sayım farkı",
    INVOICE_MATCH_DIFFERENCE: "Fatura eşleştirme farkı",
};
// ── Durumlar ────────────────────────────────────────────────────────────────
export const APPROVAL_REQUEST_STATUSES = [
    "PENDING",
    "APPROVED",
    "REJECTED",
    "CANCELLED",
    "AUTO_APPROVED",
];
export const approvalRequestStatusSchema = z.enum(APPROVAL_REQUEST_STATUSES);
export const APPROVAL_REQUEST_STATUS_LABELS = {
    PENDING: "Onay bekliyor",
    APPROVED: "Onaylandı",
    REJECTED: "Reddedildi",
    CANCELLED: "Geri çekildi",
    AUTO_APPROVED: "Otomatik onaylandı",
};
export const APPROVAL_STEP_STATUSES = ["WAITING", "ACTIVE", "APPROVED", "REJECTED", "SKIPPED"];
export const approvalStepStatusSchema = z.enum(APPROVAL_STEP_STATUSES);
export const APPROVAL_STEP_STATUS_LABELS = {
    WAITING: "Sırada",
    ACTIVE: "Karar bekliyor",
    APPROVED: "Onaylandı",
    REJECTED: "Reddedildi",
    SKIPPED: "Atlandı",
};
export const APPROVAL_STEP_MODES = ["ANY", "ALL"];
export const approvalStepModeSchema = z.enum(APPROVAL_STEP_MODES);
export const APPROVAL_STEP_MODE_LABELS = {
    ANY: "Herhangi biri onaylasın",
    ALL: "Tümü onaylasın",
};
export const APPROVAL_TIMEOUT_ACTIONS = ["NONE", "ESCALATE", "AUTO_REJECT"];
export const approvalTimeoutActionSchema = z.enum(APPROVAL_TIMEOUT_ACTIONS);
export const APPROVAL_TIMEOUT_ACTION_LABELS = {
    NONE: "Beklemeye devam etsin",
    ESCALATE: "Üst onaycıya yükselt",
    AUTO_REJECT: "Otomatik reddet",
};
export const APPROVAL_APPROVER_KINDS = ["ROLE", "USER", "PERMISSION"];
export const approvalApproverKindSchema = z.enum(APPROVAL_APPROVER_KINDS);
export const APPROVAL_APPROVER_KIND_LABELS = {
    ROLE: "Rol",
    USER: "Kişi",
    PERMISSION: "Yetki",
};
export const APPROVAL_DECISIONS = [
    "APPROVE",
    "REJECT",
    "CANCEL",
    "ESCALATE",
    "AUTO_REJECT",
    "AUTO_APPROVE",
];
export const approvalDecisionKindSchema = z.enum(APPROVAL_DECISIONS);
export const APPROVAL_DECISION_LABELS = {
    APPROVE: "Onayladı",
    REJECT: "Reddetti",
    CANCEL: "Geri çekti",
    ESCALATE: "Süre doldu, yükseltildi",
    AUTO_REJECT: "Süre doldu, otomatik reddedildi",
    AUTO_APPROVE: "Politika yok, otomatik onaylandı",
};
/** Talebin sonucu: karar kesinleşince talep eden modülün işleyicisine bu değer iletilir. */
export const APPROVAL_OUTCOMES = ["APPROVED", "REJECTED", "CANCELLED"];
export const approvalOutcomeSchema = z.enum(APPROVAL_OUTCOMES);
// ── Politikalar ─────────────────────────────────────────────────────────────
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
/**
 * Bir adımın onaycısı: rol (o role atanmış etkin üyeler), kişi ya da izin (o izni taşıyan rollerdeki üyeler).
 * `isEscalation` işaretli onaycılar YALNIZCA adım süresi dolup yükseltildikten sonra karar verebilir.
 */
export const approvalPolicyApproverInputSchema = z
    .object({
    approverKind: approvalApproverKindSchema,
    roleId: uuidSchema.nullable().default(null),
    userId: uuidSchema.nullable().default(null),
    permission: permissionSchema.nullable().default(null),
    isEscalation: z.boolean().default(false),
})
    .refine((value) => value.approverKind === "ROLE"
    ? value.roleId !== null && value.userId === null && value.permission === null
    : value.approverKind === "USER"
        ? value.userId !== null && value.roleId === null && value.permission === null
        : value.permission !== null && value.roleId === null && value.userId === null, "Onaycı türüyle doldurulan alan uyuşmuyor (ROLE → roleId, USER → userId, PERMISSION → permission)");
export const approvalPolicyStepInputSchema = z
    .object({
    stepNo: z.number().int().min(1).max(20),
    name: optionalText(100),
    mode: approvalStepModeSchema.default("ANY"),
    timeoutHours: z.number().int().min(1).max(2160).nullable().default(null),
    onTimeout: approvalTimeoutActionSchema.default("NONE"),
    approvers: z.array(approvalPolicyApproverInputSchema).min(1, "Adımda en az bir onaycı olmalıdır").max(20),
})
    .refine((value) => value.onTimeout === "NONE" || value.timeoutHours !== null, "Zaman aşımı eylemi seçildiyse süre (saat) girilmelidir")
    .refine((value) => value.approvers.some((approver) => !approver.isEscalation), "Adımda en az bir asıl (yükseltme olmayan) onaycı olmalıdır");
const policyBodyShape = {
    docType: approvalDocTypeSchema,
    name: z.string().trim().min(1).max(150),
    description: optionalText(500),
    priority: z.number().int().min(0).max(1000).default(0),
    companyId: uuidSchema.nullable().default(null).describe("null = kiracıdaki tüm şirketler"),
    stationIds: z.array(uuidSchema).max(500).default([]).describe("boş = tüm istasyonlar"),
    minAmount: moneySchema.nullable().default(null).describe("Kuruş; alt sınır DÂHİL"),
    maxAmount: moneySchema.nullable().default(null).describe("Kuruş; üst sınır HARİÇ"),
    requiresStepUp: z.boolean().default(false),
    isActive: z.boolean().default(true),
    steps: z.array(approvalPolicyStepInputSchema).min(1, "En az bir adım tanımlayın").max(20),
};
const withPolicyRules = (schema) => schema
    .refine((value) => {
    const body = value;
    return (body.minAmount === null || body.maxAmount === null || BigInt(body.maxAmount) > BigInt(body.minAmount));
}, "Üst sınır alt sınırdan büyük olmalıdır")
    .refine((value) => {
    const steps = value.steps;
    return new Set(steps.map((step) => step.stepNo)).size === steps.length;
}, "Adım numaraları benzersiz olmalıdır");
export const createApprovalPolicySchema = withPolicyRules(z.object(policyBodyShape));
/**
 * PUT: politikanın TAMAMINI (adımlar ve onaycılar dâhil) değiştirir — kısmi güncelleme değildir, bu yüzden adı
 * `update…Schema` DEĞİLDİR (`common/patch.test.ts` yalnızca PATCH şemalarını denetler).
 */
export const saveApprovalPolicySchema = withPolicyRules(z.object({ ...policyBodyShape, version: z.number().int().positive() }));
export const approvalPolicyApproverSchema = z.object({
    approverKind: approvalApproverKindSchema,
    roleId: uuidSchema.nullable(),
    roleName: z.string().nullable(),
    userId: uuidSchema.nullable(),
    userName: z.string().nullable(),
    permission: z.string().nullable(),
    isEscalation: z.boolean(),
});
export const approvalPolicyStepSchema = z.object({
    id: uuidSchema,
    stepNo: z.number().int(),
    name: z.string().nullable(),
    mode: approvalStepModeSchema,
    timeoutHours: z.number().int().nullable(),
    onTimeout: approvalTimeoutActionSchema,
    approvers: z.array(approvalPolicyApproverSchema),
});
export const approvalPolicySchema = z.object({
    id: uuidSchema,
    docType: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    priority: z.number().int(),
    companyId: uuidSchema.nullable(),
    companyName: z.string().nullable(),
    stationIds: z.array(uuidSchema),
    minAmount: moneySchema.nullable(),
    maxAmount: moneySchema.nullable(),
    requiresStepUp: z.boolean(),
    isActive: z.boolean(),
    isSystemDefault: z.boolean(),
    steps: z.array(approvalPolicyStepSchema),
    version: z.number().int(),
    createdAt: timestampSchema,
});
export const approvalPolicyListSchema = z.object({ items: z.array(approvalPolicySchema) });
export const listApprovalPoliciesQuerySchema = z.object({
    docType: approvalDocTypeSchema.optional(),
    companyId: uuidSchema.optional(),
    includeInactive: z
        .enum(["true", "false"])
        .default("false")
        .transform((value) => value === "true"),
});
// ── Vekâlet ─────────────────────────────────────────────────────────────────
export const createApprovalDelegationSchema = z
    .object({
    delegatorUserId: uuidSchema
        .nullable()
        .default(null)
        .describe("Yetkisini devreden; null = kendisi. Başkası adına tanımlamak ayrı yetki ister."),
    delegateUserId: uuidSchema.describe("Vekil"),
    validFrom: timestampSchema,
    validTo: timestampSchema.nullable().default(null).describe("null = açık uçlu"),
    docTypes: z.array(approvalDocTypeSchema).max(20).default([]).describe("boş = tüm belge türleri"),
    reason: optionalText(500),
})
    .refine((value) => value.validTo === null || new Date(value.validTo) > new Date(value.validFrom), "Bitiş tarihi başlangıçtan sonra olmalıdır")
    .refine((value) => value.delegatorUserId === null || value.delegatorUserId !== value.delegateUserId, "Kişi kendine vekâlet veremez");
export const approvalDelegationSchema = z.object({
    id: uuidSchema,
    delegatorUserId: uuidSchema,
    delegatorName: z.string(),
    delegateUserId: uuidSchema,
    delegateName: z.string(),
    validFrom: timestampSchema,
    validTo: timestampSchema.nullable(),
    docTypes: z.array(z.string()),
    reason: z.string().nullable(),
    isActive: z.boolean(),
    revokedAt: timestampSchema.nullable(),
    version: z.number().int(),
    createdAt: timestampSchema,
});
export const approvalDelegationListSchema = z.object({ items: z.array(approvalDelegationSchema) });
export const listApprovalDelegationsQuerySchema = z.object({
    scope: z
        .enum(["MINE", "ALL"])
        .default("MINE")
        .describe("MINE: devrettiklerim ve bana devredilenler · ALL: kiracıdaki tümü (ayrı yetki)"),
    includeInactive: z
        .enum(["true", "false"])
        .default("false")
        .transform((value) => value === "true"),
});
// ── Talepler ────────────────────────────────────────────────────────────────
export const approvalRequestSummarySchema = z.object({
    id: uuidSchema,
    docType: z.string(),
    docId: uuidSchema,
    docNo: z.string().nullable(),
    title: z.string(),
    amount: moneySchema.nullable(),
    companyId: uuidSchema,
    companyName: z.string().nullable(),
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    status: approvalRequestStatusSchema,
    requestedById: uuidSchema,
    requestedByName: z.string().nullable(),
    requestedAt: timestampSchema,
    policyName: z.string().nullable(),
    requiresStepUp: z.boolean(),
    currentStep: z.number().int().nullable(),
    /** Etkin adımın son karar zamanı (zaman aşımı tanımlıysa). */
    dueAt: timestampSchema.nullable(),
    decidedAt: timestampSchema.nullable(),
    decisionNote: z.string().nullable(),
    version: z.number().int(),
});
export const approvalRequestPageSchema = pageOf(approvalRequestSummarySchema);
export const approvalDecisionSchema = z.object({
    id: uuidSchema,
    stepNo: z.number().int().nullable(),
    decision: approvalDecisionKindSchema,
    decidedById: uuidSchema.nullable(),
    decidedByName: z.string().nullable(),
    onBehalfOfId: uuidSchema.nullable(),
    onBehalfOfName: z.string().nullable(),
    /** Görüntüleme metni: vekâletle verilen kararda "Devreden adına Vekil". */
    actorLabel: z.string(),
    note: z.string().nullable(),
    clientKind: z.string().nullable(),
    createdAt: timestampSchema,
});
export const approvalRequestStepSchema = z.object({
    id: uuidSchema,
    stepNo: z.number().int(),
    name: z.string().nullable(),
    mode: approvalStepModeSchema,
    status: approvalStepStatusSchema,
    timeoutHours: z.number().int().nullable(),
    onTimeout: approvalTimeoutActionSchema,
    dueAt: timestampSchema.nullable(),
    escalatedAt: timestampSchema.nullable(),
    approvers: z.array(approvalPolicyApproverSchema).describe("Politikanın talep anındaki fotoğrafı"),
    /** Onaycı tanımlarının ŞU ANKİ kullanıcı karşılıkları (ad soyad). */
    resolvedApprovers: z.array(z.object({ userId: uuidSchema, name: z.string() })),
    decisions: z.array(approvalDecisionSchema),
});
export const approvalRequestDetailSchema = approvalRequestSummarySchema.extend({
    summary: z.record(z.string(), z.unknown()),
    steps: z.array(approvalRequestStepSchema),
    decisions: z.array(approvalDecisionSchema),
    /** Bu kullanıcı şu anda karar verebilir mi (ve kimlerin adına)? */
    canDecide: z.boolean(),
    decidableOnBehalfOf: z.array(z.object({ userId: uuidSchema, name: z.string() })),
});
export const approvalDecisionBodySchema = z
    .object({
    decision: z.enum(["APPROVE", "REJECT"]),
    note: optionalText(1000),
    onBehalfOf: uuidSchema
        .nullable()
        .default(null)
        .describe("Vekâletle karar verirken yetkisi kullanılan onaycı"),
    version: z.number().int().positive(),
})
    .refine((value) => value.decision !== "REJECT" || (value.note !== null && value.note.length >= 3), "Red gerekçesi zorunludur (en az 3 karakter)");
export const cancelApprovalRequestSchema = z.object({
    note: optionalText(1000).describe("Geri çekme gerekçesi"),
    version: z.number().int().positive(),
});
export const APPROVAL_LIST_SCOPES = ["MINE_TO_DECIDE", "MY_REQUESTS", "ALL"];
export const approvalListScopeSchema = z.enum(APPROVAL_LIST_SCOPES);
export const APPROVAL_LIST_SCOPE_LABELS = {
    MINE_TO_DECIDE: "Bana bekleyenler",
    MY_REQUESTS: "Taleplerim",
    ALL: "Tüm talepler",
};
export const listApprovalRequestsQuerySchema = pageQuerySchema.extend({
    scope: approvalListScopeSchema.default("MINE_TO_DECIDE"),
    status: approvalRequestStatusSchema.optional(),
    docType: approvalDocTypeSchema.optional(),
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
});
/** Panodaki rozet: karar bekleyen talep sayısı. */
export const approvalPendingCountSchema = z.object({ count: z.number().int() });
//# sourceMappingURL=approvals.js.map